import type { EmailData } from "../types"
import { ItemType, ReviewStatus } from "../types/shared"

export interface CombinedItem {
  id: number
  item_id: number
  review_id: number
  type: ItemType
  amount: number
  operator: string
  review_status: ReviewStatus
  review_notes?: string | null
  reviewed_at?: string | null
  reviewed_by?: string | null
  updated_at?: string
  created_at?: string
  deleted_at?: string | null
  files?: any[]
  review_notes_history?: any[]
  name?: string
  number?: string
  store_justification?: string
  store_code?: number
  conciliation_id?: number
  // Campo do email para mostrar "Última solicitação"
  email_sent_at?: string
}

export const combineEmailItems = (email: EmailData): CombinedItem[] => {
  // sent_at por review (vindo do backend) reflete o momento real do
  // envio individual daquele item. Quando ainda não preenchido (registros
  // anteriores ao fix de granularidade), faz fallback para o sent_at da
  // solicitação inteira.
  const divergences = email.divergences.map((d) => ({
    ...d,
    type: ItemType.DIVERGENCE as const,
    email_sent_at: d.sent_at ?? email.sent_at,
  }))

  const nsus = email.nsus.map((n) => ({
    ...n,
    type: ItemType.NSU as const,
    email_sent_at: n.sent_at ?? email.sent_at,
  }))

  return [...divergences, ...nsus]
}

/**
 * Retorna a data da última solicitação do item
 * Para itens pendentes de resposta, usa a data de envio do email (sent_at)
 * Para itens já respondidos, usa a data de atualização do item
 */
export const getItemLastUpdate = (item: CombinedItem): string => {
  // Prioriza o sent_at do email para mostrar a última solicitação
  if (item.email_sent_at) {
    return item.email_sent_at
  }
  return item.updated_at || item.created_at || new Date().toISOString()
}

export const isItemAlreadyReviewed = (status: ReviewStatus): boolean => {
  return status === ReviewStatus.REJECTED || status === ReviewStatus.APPROVED
}

export const canItemBeSelected = (status: ReviewStatus): boolean => {
  return status === ReviewStatus.RESPONDED ||
         status === ReviewStatus.REJECTED_WAITING_FOR_RESPOND
}

/**
 * Verifica se um item pode ser reenviado
 * Apenas itens com status PENDING_ANSWER ou REJECTED_WAITING_FOR_RESPOND podem ser reenviados
 */
export const canItemBeResent = (status: ReviewStatus): boolean => {
  return status === ReviewStatus.PENDING_ANSWER ||
         status === ReviewStatus.REJECTED_WAITING_FOR_RESPOND
}

/**
 * Retorna os itens que podem ser reenviados
 */
export const getResendableItems = (allItems: CombinedItem[]): CombinedItem[] => {
  return allItems.filter(item => canItemBeResent(item.review_status))
}

export const hasRejectedWaitingStatus = (
  selectedItems: Set<number>,
  allItems: CombinedItem[]
): boolean => {
  return Array.from(selectedItems).some(itemId => {
    const item = allItems.find(i => i.id === itemId)
    return item?.review_status === ReviewStatus.REJECTED_WAITING_FOR_RESPOND
  })
}

export const getSelectableItems = (allItems: CombinedItem[]): CombinedItem[] => {
  return allItems.filter(item => canItemBeSelected(item.review_status))
}

export const getItemDisplayName = (item: CombinedItem): string => {
  return item.type === ItemType.DIVERGENCE ? (item.name || '') : (item.number || '')
}

export const getItemTypeLabel = (type: ItemType): string => {
  return type === ItemType.DIVERGENCE ? "Divergência" : "NSU"
}

/**
 * Verifica se o item pode ter histórico visível
 * - Itens com review_notes_history sempre podem
 * - Itens PENDING_ANSWER ou REJECTED_WAITING_FOR_RESPOND podem ter audit logs (EMAIL_SENT, EMAIL_RESENT)
 */
export const canShowHistory = (item: CombinedItem): boolean => {
  const hasReviewHistory = Array.isArray(item.review_notes_history) && item.review_notes_history.length > 0
  const canHaveAuditLogs = canItemBeResent(item.review_status)

  return hasReviewHistory || canHaveAuditLogs
}
